import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Navigation, 
  Phone, 
  Clock, 
  MapPin, 
  RefreshCw,
  Car,
  Thermometer,
  Wind,
  Loader2,
  ExternalLink,
  Radio
} from 'lucide-react';
import { loadGoogleMaps } from '@/utils/loadGoogleMaps';
import { fitMapToMarkers, syncActiveServicesOnMap } from '@/utils/liveVehicleMapLayers';
import { updateDrivingRoutesOnMap } from '@/utils/drivingRoute';

const GOOGLE_MAPS_KEY = (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '').trim();
const DEFAULT_CENTER = { lat: 40.4168, lng: -3.7038 };
const DEFAULT_ZOOM = 6;

function getAdminBackendBaseUrl() {
  const env = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/+$/, '');
  if (env) return env;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return String(window.location.origin).replace(/\/+$/, '');
  }
  return '';
}

/** Normalize current_location from API (lat/lng or latitude/longitude). */
function getGpsCoords(service) {
  const c = service?.current_location;
  if (!c || typeof c !== 'object') return null;
  const lat = c.lat ?? c.latitude;
  const lng = c.lng ?? c.longitude;
  if (lat == null || lng == null) return null;
  const la = parseFloat(lat);
  const ln = parseFloat(lng);
  if (!Number.isFinite(la) || !Number.isFinite(ln)) return null;
  return { lat: la, lng: ln, updated_at: c.updated_at, speed: c.speed, heading: c.heading };
}

/** Pickup / delivery points may use lat/lng or latitude/longitude (same as current_location). */
function getLocationPointCoords(loc) {
  if (!loc || typeof loc !== 'object') return null;
  const lat = loc.lat ?? loc.latitude;
  const lng = loc.lng ?? loc.longitude;
  if (lat == null || lng == null) return null;
  const la = parseFloat(lat);
  const ln = parseFloat(lng);
  if (!Number.isFinite(la) || !Number.isFinite(ln)) return null;
  return { lat: la, lng: ln };
}

/**
 * Free map preview: official OSM embed (no API key). More reliable than third-party static PNG services.
 * bbox = min_lon, min_lat, max_lon, max_lat
 */
function computeBounds(points) {
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  for (const p of points) {
    minLat = Math.min(minLat, p.lat);
    maxLat = Math.max(maxLat, p.lat);
    minLng = Math.min(minLng, p.lng);
    maxLng = Math.max(maxLng, p.lng);
  }
  const dLat = Math.max(maxLat - minLat, 0.003);
  const dLng = Math.max(maxLng - minLng, 0.003);
  const padLat = dLat * 0.2;
  const padLng = dLng * 0.2;
  return {
    minLat: minLat - padLat,
    maxLat: maxLat + padLat,
    minLng: minLng - padLng,
    maxLng: maxLng + padLng,
    centerLat: (minLat + maxLat) / 2,
    centerLng: (minLng + maxLng) / 2,
  };
}

/** OSM embed iframe — bbox only; optional single marker query param. */
function buildOsmEmbedUrl(points, markerPoint = null) {
  if (!points.length) return null;
  const b = computeBounds(points);
  const bbox = `${b.minLng},${b.minLat},${b.maxLng},${b.maxLat}`;
  let url = `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik`;
  if (markerPoint) {
    url += `&marker=${encodeURIComponent(`${markerPoint.lat},${markerPoint.lng}`)}`;
  }
  return url;
}

const GPSLiveTab = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = (i18n.language || '').startsWith('en') ? 'en-US' : 'es-ES';
  const [activeServices, setActiveServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [weatherData, setWeatherData] = useState({});
  const [selectedService, setSelectedService] = useState(null);
  const [mapInitialized, setMapInitialized] = useState(false);
  const [mapsLoadFailed, setMapsLoadFailed] = useState(false);
  const [etaData, setEtaData] = useState({});

  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const overlaysRef = useRef([]);
  const routeOverlaysRef = useRef([]);
  const routeFetchSeqRef = useRef(0);

  const fetchActiveServices = useCallback(async () => {
    const base = getAdminBackendBaseUrl();
    if (!base) {
      setActiveServices([]);
      setLoading(false);
      setRefreshing(false);
      return;
    }
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${base}/api/admin/transport-services/active`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setActiveServices(data.services || []);
        setLastUpdate(new Date());
        
        for (const service of (data.services || [])) {
          const g = getGpsCoords(service);
          if (g) {
            fetchWeather(base, service.id, g.lat, g.lng);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching active services:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const fetchWeather = async (base, serviceId, lat, lng) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${base}/api/weather?lat=${lat}&lng=${lng}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setWeatherData(prev => ({ ...prev, [serviceId]: data }));
      }
    } catch (error) {
      console.error('Error fetching weather:', error);
    }
  };

  useEffect(() => {
    fetchActiveServices();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchActiveServices();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchActiveServices]);

  const initializeMap = useCallback((container) => {
    if (!container || mapInstanceRef.current || !window.google?.maps) return;
    mapInstanceRef.current = new window.google.maps.Map(container, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
    });
    setMapInitialized(true);
  }, []);

  const mapRef = useCallback((node) => {
    mapContainerRef.current = node;
    if (node && window.google?.maps && !mapInstanceRef.current) {
      initializeMap(node);
    }
  }, [initializeMap]);

  useEffect(() => {
    if (!GOOGLE_MAPS_KEY) return;
    loadGoogleMaps()
      .then(() => {
        setMapsLoadFailed(false);
        if (mapContainerRef.current && !mapInstanceRef.current) {
          initializeMap(mapContainerRef.current);
        }
      })
      .catch(() => setMapsLoadFailed(true));
  }, [initializeMap]);

  useEffect(() => {
    if (!GOOGLE_MAPS_KEY) return undefined;
    const intervalId = setInterval(() => {
      if (window.google?.maps && mapContainerRef.current && !mapInstanceRef.current) {
        initializeMap(mapContainerRef.current);
        clearInterval(intervalId);
      }
    }, 200);
    return () => clearInterval(intervalId);
  }, [initializeMap]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchActiveServices();
  };

  const formatTime = (isoString) => {
    if (!isoString) return t('gps_live_na');
    const date = new Date(isoString);
    return date.toLocaleTimeString(dateLocale, { hour: '2-digit', minute: '2-digit' });
  };

  const mapMeta = useMemo(() => {
    let hasLiveGps = false;
    let hasRoutePoints = false;
    const routePts = [];
    let openQuery = null;

    for (const s of activeServices) {
      const g = getGpsCoords(s);
      if (g) {
        hasLiveGps = true;
        if (!openQuery) openQuery = `${g.lat},${g.lng}`;
      }
      const p = getLocationPointCoords(s.pickup_location);
      const d = getLocationPointCoords(s.delivery_location);
      if (p) {
        hasRoutePoints = true;
        routePts.push(p);
        if (!openQuery) openQuery = `${p.lat},${p.lng}`;
      }
      if (d) {
        hasRoutePoints = true;
        routePts.push(d);
        if (!openQuery) openQuery = `${d.lat},${d.lng}`;
      }
    }

    const embedUrl = routePts.length
      ? buildOsmEmbedUrl(routePts, routePts.length === 1 ? routePts[0] : null)
      : null;

    return {
      mode: hasLiveGps ? 'live' : hasRoutePoints ? 'route' : 'none',
      embedUrl,
      openQuery,
      hasRoutePoints,
    };
  }, [activeServices]);

  useEffect(() => {
    if (!mapInstanceRef.current || !mapInitialized) return;

    const bounds = new window.google.maps.LatLngBounds();
    const { hasValidLocations, livePositions } = syncActiveServicesOnMap({
      map: mapInstanceRef.current,
      activeServices,
      markersRef,
      overlaysRef,
      bounds,
      getCurrentGps: getGpsCoords,
      getPickupGps: (s) => getLocationPointCoords(s.pickup_location),
      getDeliveryGps: (s) => getLocationPointCoords(s.delivery_location),
      getPlate: (s) => s.vehicle_info?.plate,
      t,
      onLiveVehicleClick: (service) => setSelectedService(service.id),
    });

    if (hasValidLocations) {
      fitMapToMarkers(mapInstanceRef.current, bounds, { livePositions });
    }

    let cancelled = false;
    const seq = ++routeFetchSeqRef.current;
    updateDrivingRoutesOnMap({
      map: mapInstanceRef.current,
      activeServices,
      routeOverlaysRef,
      getCurrentGps: getGpsCoords,
      getDeliveryGps: (s) => getLocationPointCoords(s.delivery_location),
    }).then((etas) => {
      if (!cancelled && seq === routeFetchSeqRef.current) setEtaData(etas);
    });

    return () => {
      cancelled = true;
    };
  }, [activeServices, mapInitialized, t]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">{t('gps_live_loading')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="gps-live-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Navigation className="w-6 h-6 text-blue-600" />
            {t('gps_live_title')}
          </h2>
          <p className="text-gray-600 mt-1">
            {t('gps_live_subtitle')}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Live Indicator */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 rounded-full">
            <Radio className="w-4 h-4 text-green-600 animate-pulse" />
            <span className="text-sm font-medium text-green-700">{t('gps_live_badge')}</span>
          </div>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {t('gps_live_refresh')}
          </Button>
        </div>
      </div>

      {/* Last Update Info */}
      {lastUpdate && (
        <p className="text-sm text-gray-500">
          {t('gps_live_last_update_full', { time: lastUpdate.toLocaleTimeString(dateLocale) })}
        </p>
      )}

      {/* Map */}
      {activeServices.length > 0 ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              {t('gps_live_map_title', { count: activeServices.length })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {GOOGLE_MAPS_KEY && !mapsLoadFailed ? (
              <>
              <div className="relative rounded-lg border overflow-hidden bg-gray-50">
                  <div
                    ref={mapRef}
                    className="w-full h-[420px] bg-gray-100"
                    data-testid="admin-gps-map"
                  />
                  {!mapInitialized && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                      <span className="ml-2 text-gray-600">{t('client_gps_loading_map')}</span>
                    </div>
                  )}
                  {mapMeta.openQuery && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${mapMeta.openQuery}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute bottom-2 right-2 bg-white px-3 py-1.5 rounded-lg shadow text-sm font-medium text-blue-600 hover:bg-blue-50 flex items-center gap-1 z-10"
                >
                  <ExternalLink className="w-4 h-4" />
                  {t('gps_live_open_maps')}
                </a>
                  )}
              </div>
                <div className="mt-3 flex flex-wrap gap-4 text-xs text-gray-600">
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-full bg-green-500 inline-block" />
                    {t('gps_live_origin')}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-full bg-red-500 inline-block" />
                    {t('gps_live_destination')}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="relative flex h-4 w-4 items-center justify-center">
                      <span className="absolute h-4 w-4 rounded-full bg-green-400/40 animate-pulse" />
                      <Car className="relative w-3 h-3 text-blue-600" />
                    </span>
                    {t('gps_map_live_vehicle', { defaultValue: 'Live vehicle (highlighted)' })}
                  </span>
                  <span className="flex items-center gap-1.5 text-blue-700">
                    <span className="w-6 border-t-2 border-dashed border-blue-600 inline-block" />
                    {t('gps_map_route_to_destination', { defaultValue: 'Route to destination' })}
                  </span>
                </div>
                {mapMeta.mode === 'route' && (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-3 py-2 mt-2">
                    {t('gps_live_map_route_hint')}
                  </p>
                )}
              </>
            ) : mapMeta.embedUrl ? (
              <>
                <div className="relative rounded-lg border overflow-hidden bg-gray-50">
                  <iframe
                    title={t('gps_live_map_alt')}
                    src={mapMeta.embedUrl}
                    className="w-full h-[420px] border-0 block"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                  {mapMeta.openQuery && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${mapMeta.openQuery}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute bottom-2 right-2 bg-white px-3 py-1.5 rounded-lg shadow text-sm font-medium text-blue-600 hover:bg-blue-50 flex items-center gap-1 z-10"
                    >
                      <ExternalLink className="w-4 h-4" />
                      {t('gps_live_open_maps')}
                    </a>
                  )}
                </div>
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-3 py-2 mt-2">
                  {mapsLoadFailed
                    ? t('gps_live_maps_js_failed', {
                        defaultValue:
                          'Google Maps could not load. Check REACT_APP_GOOGLE_MAPS_API_KEY, enable Maps JavaScript API, and restart the frontend.',
                      })
                    : t('gps_live_static_map_key_missing')}
                </p>
                {mapMeta.mode === 'route' && (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-3 py-2 mt-2">
                    {t('gps_live_map_route_hint')}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">{t('gps_live_map_osm_attribution')}</p>
              </>
            ) : (
              <div className="h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                <p className="text-gray-500">{t('gps_live_no_locations')}</p>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Car className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900">{t('gps_live_empty_title')}</h3>
              <p className="text-gray-500 mt-1">
                {t('gps_live_empty_description')}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Services List */}
      {activeServices.length > 0 && (
        <div className="grid gap-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {t('gps_live_active_services', { count: activeServices.length })}
          </h3>
          
          {activeServices.map((service) => (
            <Card 
              key={service.id} 
              className={`cursor-pointer transition-all hover:shadow-md ${selectedService === service.id ? 'ring-2 ring-blue-500' : ''}`}
              onClick={() => setSelectedService(selectedService === service.id ? null : service.id)}
            >
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* Vehicle & Driver Info */}
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <Car className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-lg">{service.vehicle_info?.plate || t('no_plate')}</span>
                        <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300">
                          {t('gps_live_in_route')}
                        </Badge>
                      </div>
                      <p className="text-gray-600">
                        {service.vehicle_info?.brand} {service.vehicle_info?.model}
                        {service.vehicle_info?.color && ` - ${service.vehicle_info.color}`}
                      </p>
                      {service.driver_info && (
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className="font-medium">{service.driver_info.name}</span>
                          {service.driver_info.phone && (
                            <a 
                              href={`tel:${service.driver_info.phone}`} 
                              className="flex items-center gap-1 text-blue-600 hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Phone className="w-3 h-3" />
                              {service.driver_info.phone}
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Route Info */}
                  <div className="flex flex-col gap-1 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      <span className="text-gray-600 truncate max-w-xs">
                        {service.pickup_location?.address || t('gps_live_origin')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      <span className="text-gray-600 truncate max-w-xs">
                        {service.delivery_location?.address || t('gps_live_destination')}
                      </span>
                    </div>
                  </div>

                  {/* GPS & ETA Info */}
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    {(() => {
                      const g = getGpsCoords(service);
                      return g ? (
                      <div className="flex items-center gap-1 text-gray-600">
                        <MapPin className="w-4 h-4 text-blue-500" />
                        <span>
                          {g.lat.toFixed(4)}, {g.lng.toFixed(4)}
                        </span>
                      </div>
                      ) : null;
                    })()}
                    
                    {(() => {
                      const routeEta = etaData[service.id];
                      const minutes =
                        routeEta?.durationValue != null
                          ? Math.max(1, Math.round(routeEta.durationValue / 60))
                          : service.eta_minutes != null && Number.isFinite(Number(service.eta_minutes))
                            ? Math.round(Number(service.eta_minutes))
                            : null;
                      if (!minutes && !routeEta?.durationText) return null;
                      return (
                      <div className="flex items-center gap-1 px-2 py-1 bg-orange-100 rounded text-orange-700">
                        <Clock className="w-4 h-4" />
                        <span className="font-medium">
                          {routeEta?.durationText
                            ? `${routeEta.durationText}${routeEta.distanceText ? ` · ${routeEta.distanceText}` : ''}`
                            : t('gps_live_eta', { minutes })}
                        </span>
                      </div>
                      );
                    })()}

                    {/* Weather */}
                    {weatherData[service.id] && !weatherData[service.id].error && (
                      <div className="flex items-center gap-2 px-2 py-1 bg-gray-100 rounded">
                        <Thermometer className="w-4 h-4 text-gray-500" />
                        <span>{weatherData[service.id].temperature}°C</span>
                        <Wind className="w-4 h-4 text-gray-500 ml-2" />
                        <span>{weatherData[service.id].wind_speed} m/s</span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {service.tracking_url && (
                      <a 
                        href={service.tracking_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button variant="outline" size="sm">
                          <ExternalLink className="w-4 h-4 mr-1" />
                          {t('gps_live_tracking_btn')}
                        </Button>
                      </a>
                    )}
                  </div>
                </div>

                {/* Expanded Details */}
                {selectedService === service.id && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">{t('gps_live_last_gps_update')}</span>
                        <p className="font-medium">{formatTime(service.current_location?.updated_at)}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">{t('gps_live_speed')}</span>
                        <p className="font-medium">
                          {service.current_location?.speed ? `${service.current_location.speed.toFixed(1)} km/h` : t('gps_live_na')}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">{t('gps_live_scheduled_date')}</span>
                        <p className="font-medium">{service.scheduled_datetime || t('gps_live_na')}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">{t('gps_live_service_id')}</span>
                        <p className="font-medium text-xs">{service.id}</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default GPSLiveTab;
